import { CheckCircle2, ExternalLink, Send, ShieldAlert } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { AppLayout } from "~/components/layout/app-layout";
import { envConfig } from "~/lib/env-validation";
import { useAuth } from "~/lib/hooks/use-auth";
import type { Route } from "./+types/report-data-issue";

type CorrectionSubject = "COMPANY" | "PERSON" | "INSOLVENCY" | "VAT" | "SCORE";

const SUBJECTS: { value: CorrectionSubject; label: string }[] = [
	{ value: "COMPANY", label: "Základní údaj o firmě" },
	{ value: "PERSON", label: "Osobní údaj" },
	{ value: "INSOLVENCY", label: "Insolvenční řízení" },
	{ value: "VAT", label: "Registrace nebo spolehlivost DPH" },
	{ value: "SCORE", label: "Rizikové hodnocení" },
];

export function meta(_: Route.MetaArgs) {
	return [
		{ title: "Nahlásit nesprávný údaj | FirmIndex" },
		{ name: "robots", content: "noindex, nofollow" },
	];
}

function correctionSubject(value: string | null): CorrectionSubject {
	return SUBJECTS.some((subject) => subject.value === value)
		? (value as CorrectionSubject)
		: "COMPANY";
}

export default function ReportDataIssueRoute() {
	const [params] = useSearchParams();
	const { session } = useAuth();
	const [subject, setSubject] = useState<CorrectionSubject>(
		correctionSubject(params.get("subject")),
	);
	const [description, setDescription] = useState("");
	const [evidenceUrl, setEvidenceUrl] = useState("");
	const [attested, setAttested] = useState(false);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [requestId, setRequestId] = useState<string | null>(null);

	const companyIco = params.get("companyIco") ?? "";
	const companyName = params.get("companyName") ?? "";
	const personId = params.get("personId") ?? "";
	const targetRecordId = params.get("targetRecordId") ?? "";
	const rawReturnTo = params.get("returnTo") ?? "/dashboard";
	const returnTo =
		rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") ? rawReturnTo : "/dashboard";

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!session?.access_token) {
			setError("Přihlášení vypršelo. Přihlaste se prosím znovu.");
			return;
		}
		setPending(true);
		setError(null);
		try {
			const response = await fetch(`${envConfig.apiBaseUrl}/corrections`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${session.access_token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					subject,
					...(companyIco ? { companyIco } : {}),
					...(personId ? { personId } : {}),
					...(targetRecordId ? { targetRecordId } : {}),
					description,
					...(evidenceUrl ? { evidenceUrl } : {}),
					attested,
				}),
			});
			const body = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
			if (!response.ok || !body.id) throw new Error(body.error ?? "Podnět se nepodařilo odeslat.");
			setRequestId(body.id);
		} catch (submitError) {
			setError(
				submitError instanceof Error ? submitError.message : "Podnět se nepodařilo odeslat.",
			);
		} finally {
			setPending(false);
		}
	}

	if (requestId) {
		return (
			<AppLayout title="Nahlásit nesprávný údaj">
				<div className="mx-auto max-w-2xl card-surface rounded-xl p-6">
					<div className="flex items-start gap-3">
						<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-50">
							<CheckCircle2 className="h-4 w-4 text-green-600" />
						</div>
						<div className="flex flex-col gap-3">
							<div className="flex flex-col gap-1">
								<h1 className="text-xl font-semibold">Podnět jsme přijali</h1>
								<p className="text-sm text-muted">
									Dotčené rizikové údaje jsme do prověření omezili. Číslo podnětu: {requestId}
								</p>
							</div>
							<Link to={returnTo} className="btn btn-primary btn-sm w-fit">
								Zpět na profil
							</Link>
						</div>
					</div>
				</div>
			</AppLayout>
		);
	}

	return (
		<AppLayout title="Nahlásit nesprávný údaj">
			<div className="mx-auto flex max-w-2xl flex-col gap-6">
				<div className="card-tonal rounded-xl p-4">
					<div className="flex items-start gap-3">
						<ShieldAlert className="h-5 w-5 shrink-0 text-primary-600" />
						<div className="flex flex-col gap-1">
							<p className="text-sm font-medium">Rizikový údaj dočasně omezíme</p>
							<p className="text-xs text-muted">
								Po odeslání podnětu skryjeme dotčený rizikový údaj do doby, než jej ověříme v
								autoritativním zdroji. Běžně odpovídáme do 30 dnů.
							</p>
						</div>
					</div>
				</div>

				<form onSubmit={handleSubmit} className="card-surface rounded-xl p-6">
					<div className="flex flex-col gap-5">
						<div className="flex flex-col gap-1">
							<p className="label-overline">Dotčený záznam</p>
							<p className="text-sm font-medium">
								{companyName ||
									(companyIco
										? `Firma IČO ${companyIco}`
										: personId
											? `Osoba ${personId}`
											: "Neuvedeno")}
							</p>
						</div>

						<label className="flex flex-col gap-1.5 text-sm font-medium">
							Typ údaje
							<select
								value={subject}
								onChange={(event) => setSubject(event.target.value as CorrectionSubject)}
								className="select select-bordered w-full"
							>
								{SUBJECTS.map((item) => (
									<option key={item.value} value={item.value}>
										{item.label}
									</option>
								))}
							</select>
						</label>

						<label className="flex flex-col gap-1.5 text-sm font-medium">
							Co je nesprávně a jak má údaj správně znít?
							<textarea
								required
								minLength={20}
								maxLength={2000}
								rows={6}
								value={description}
								onChange={(event) => setDescription(event.target.value)}
								className="textarea textarea-bordered w-full"
								placeholder="Popište nesrovnalost a uveďte správný stav…"
							/>
						</label>

						<label className="flex flex-col gap-1.5 text-sm font-medium">
							Odkaz na podklad <span className="text-muted font-normal">(nepovinné)</span>
							<div className="relative">
								<ExternalLink className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
								<input
									type="url"
									value={evidenceUrl}
									onChange={(event) => setEvidenceUrl(event.target.value)}
									className="input input-bordered w-full pl-9"
									placeholder="https://…"
								/>
							</div>
						</label>

						<label className="flex items-start gap-2 text-sm">
							<input
								type="checkbox"
								checked={attested}
								onChange={(event) => setAttested(event.target.checked)}
								className="checkbox checkbox-primary checkbox-sm mt-0.5"
							/>
							<span>
								Potvrzuji, že podnět podávám v dobré víře a uvedené informace jsou pravdivé.
							</span>
						</label>

						{error && <div className="alert alert-error text-sm">{error}</div>}

						<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
							<Link to={returnTo} className="btn btn-ghost btn-sm">
								Zrušit
							</Link>
							<button
								type="submit"
								disabled={pending || !attested || description.trim().length < 20}
								className="btn btn-primary btn-sm"
							>
								{pending ? (
									<span className="loading loading-spinner loading-xs" />
								) : (
									<Send className="h-4 w-4" />
								)}
								{pending ? "Odesílám…" : "Odeslat podnět"}
							</button>
						</div>
					</div>
				</form>
			</div>
		</AppLayout>
	);
}
